import {
  Box,
  Grid,
  LuxButton,
  LuxIconButton,
  LuxModal,
  LuxModalHeader,
  Stack,
  useMediaQuery,
  useTheme,
} from '@gemini/fe-ds-core';
import { useOnlineBookingStore } from '@store/onlineBookingStore';
import {
  TabletBreakpoint,
  SmallDesktopBreakpoint,
} from '@utils/consts/breakpoints.consts';
import { ImageBffDTO } from '@utils/interfaces';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FullScreenMobileImage, GalleryImage, SelectedImage } from './styles';
import { scrollbarStyle } from '@common/utils-scrollbar';

interface GalleryImagesProps {
  open: boolean;
  onClose: () => void;
  images: ImageBffDTO[];
}

export function GalleryImagesModal({
  open,
  onClose,
  images,
}: GalleryImagesProps) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const practiceName = useOnlineBookingStore(state => state.generalInfo?.name);
  const isMobile = useMediaQuery(`(max-width:${TabletBreakpoint}px)`);
  const isSmallDesktop = useMediaQuery(
    `(max-width:${SmallDesktopBreakpoint}px)`,
  );
  const theme = useTheme();
  const { t } = useTranslation();

  const nextImage = () => {
    setSelectedIndex(prev => {
      if (prev === null) return 0;
      if (prev < images.length - 1) return prev + 1;
      return prev; // rimane fermo se è già all'ultima
    });
  };

  const prevImage = () => {
    setSelectedIndex(prev => {
      if (prev === null) return 0;
      if (prev > 0) return prev - 1;
      return prev;
    });
  };

  useEffect(() => {
    if (isMobile && open && images?.length > 0) {
      setSelectedIndex(0);
    }
  }, [isMobile, open, images]);

  return (
    <LuxModal
      size={isSmallDesktop ? 'medium' : isMobile ? 'small' : 'extra-large'}
      onClose={() => {
        setSelectedIndex(null);
        onClose();
      }}
      open={open}
      sx={{
        maxWidth: isSmallDesktop ? '100%' : 'auto',
      }}
      header={
        <LuxModalHeader
          title={practiceName}
          enableCloseIcon
          onClose={() => {
            setSelectedIndex(null);
            onClose();
          }}
        />
      }
    >
      <>
        <Box
          display="flex"
          flexDirection={isMobile ? 'column' : 'row-reverse'}
          alignItems="start"
          gap="0.5rem"
          height={isMobile ? 'auto' : '80vh'}
          paddingBottom="1rem"
        >
          {/* Colonna immagine selezionata */}
          {selectedIndex !== null && images[selectedIndex] && (
            <Box
              sx={{
                width: '100%',
                borderRadius: '1.25rem',
                //overflow: 'hidden',
                justifyContent: 'center',
                height: isMobile ? 'auto' : '80vh',
                position: 'relative',
              }}
              onClick={() => {
                if (isMobile) setIsFullScreen(true);
              }}
            >
              <SelectedImage
                src={images[selectedIndex].url}
                alt={images[selectedIndex].alt}
              />
              {!isMobile && (
                <Stack
                  position="absolute"
                  top={0}
                  left={0}
                  width="100%"
                  height="100%"
                  justifyContent="space-between"
                  sx={{ zIndex: 3, pointerEvents: 'none' }}
                >
                  {/* CloseIcon in alto a destra */}
                  <Stack
                    direction="row"
                    justifyContent="flex-end"
                    sx={{
                      width: '100%',
                      padding: '1rem',
                      pointerEvents: 'auto',
                    }}
                  >
                    <LuxIconButton
                      icon="CloseIcon"
                      onClick={() => setSelectedIndex(null)}
                      size="small"
                      sx={{
                        backgroundColor: 'rgba(0, 0, 0, 0.4)',
                        borderRadius: '50%',
                        zIndex: 4,
                        '& svg ': {
                          '& path': {
                            fill: `${theme.palette.LUX.tokens.whiteWhiteA100} !important`,
                          },
                        },
                        border: 'unset',
                      }}
                    />
                  </Stack>

                  {/* Frecce al centro dell'immagine */}
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{
                      position: 'absolute',
                      top: '30%',
                      left: 0,
                      width: '100%',
                      transform: 'translateY(-50%)',
                      padding: '0 1rem',
                      pointerEvents: 'auto',
                    }}
                  >
                    <LuxIconButton
                      icon="ChevronLeftIcon"
                      onClick={prevImage}
                      size="small"
                      sx={{
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        borderRadius: '50%',
                        '& svg ': {
                          '& path': {
                            fill: `${theme.palette.LUX.tokens.whiteWhiteA100} !important`,
                          },
                        },
                        border: 'unset',
                      }}
                      disabled={selectedIndex === 0}
                    />
                    <LuxIconButton
                      icon="ChevronRightIcon"
                      onClick={nextImage}
                      size="small"
                      sx={{
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        borderRadius: '50%',
                        '& svg ': {
                          '& path': {
                            fill: `${theme.palette.LUX.tokens.whiteWhiteA100} !important`,
                          },
                        },
                        border: 'unset',
                      }}
                      disabled={selectedIndex === images.length - 1}
                    />
                  </Stack>
                </Stack>
              )}
            </Box>
          )}
          {/* Colonna delle immagini thumbnail */}
          <Grid
            container
            display="grid"
            gap="0.5rem"
            gridTemplateColumns={
              isMobile
                ? 'repeat(2, 1fr)' // mobile: 2 colonne
                : selectedIndex !== null
                  ? 'repeat(2, 1fr)' // desktop: 2 colonne se immagine selezionata
                  : 'repeat(3, 1fr)' // desktop iniziale
            }
            sx={{
              height: '100%',
              overflowY: 'auto',
              paddingRight: '0.5rem',
              ...scrollbarStyle(theme, {
                width: '0.25rem',
                borderRadius: '1.5rem',
              }),
            }}
          >
            {images?.map((img, index) => {
              const isSelected = selectedIndex === index;
              return (
                <Grid
                  item
                  key={index}
                  sx={{
                    width: '100%',
                    height: '11.75rem',
                    // overflow: 'hidden',
                    borderRadius: '1.25rem',
                    cursor: 'pointer',
                    border: isSelected
                      ? `2px solid ${theme.palette.LUX.tokens.bordersStatusBorderSelect} `
                      : 'unset',
                  }}
                  onClick={() => setSelectedIndex(index)}
                >
                  <GalleryImage src={img?.url} alt={img?.alt} />
                </Grid>
              );
            })}
          </Grid>
        </Box>
        {isMobile && isFullScreen && selectedIndex !== null && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '-webkit-fill-available',
              height: '-webkit-fill-available',
              backgroundColor: 'rgba(0, 22, 37, 1)',
              display: 'flex',
              flexDirection: 'column',
              zIndex: 9999,
            }}
            onClick={() => setIsFullScreen(false)}
          >
            {/* Barra superiore */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{
                width: '100%',
                padding: '1rem',
                pointerEvents: 'auto',
              }}
            >
              {/* Bottone chiusura */}
              <LuxButton
                variant="ghost"
                iconStart="ChevronLeftIcon"
                onClick={e => {
                  e.stopPropagation();
                  setIsFullScreen(false);
                }}
                size="small"
                sx={{
                  color: `${theme.palette.LUX.tokens.whiteWhiteA100} !important`,
                  '& .MuiTypography-root': {
                    color: `${theme.palette.LUX.tokens.whiteWhiteA100} !important`,
                  },
                  '& svg': {
                    color: `${theme.palette.LUX.tokens.whiteWhiteA100} !important`,
                  },
                }}
              >
                {t('generalCta.back')}
              </LuxButton>

              {/*    
              <LuxIconButton
                icon="ShareIcon"
                color="#fff"
                onClick={() => {}}
                size="small"
                sx={{
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  borderRadius: '50%',
                }}
              /> */}
            </Stack>

            {/* Immagine fullscreen */}
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
              }}
            >
              <FullScreenMobileImage
                src={images[selectedIndex].url}
                alt={images[selectedIndex].alt}
              />
            </Box>
          </Box>
        )}
      </>
    </LuxModal>
  );
}
